import SoftphoneAPI from '@/api/softphone.api'
import {
  SOFTPHONE_EVENTS,
  createSoftphoneTransport,
  exposeSoftphoneDevHook,
  removeSoftphoneDevHook
} from '@/services/softphone'
import { createSoftphoneOwnership } from '@/services/softphone/softphoneOwnership'
import { armRingtone, startRinging, stopRinging } from '@/services/softphone/ringtone'
import { goOfflineWithKeepalive } from '@/services/softphone/goOfflineBeacon'
import { createSoftphoneChannel, SOFTPHONE_COMMANDS } from '@/services/softphone/softphoneChannel'

/**
 * `otherTab` is not part of the call lifecycle: it is where this tab lands when another tab takes
 * the softphone over, so two tabs can never answer for the same agent.
 */
export const SOFTPHONE_STATUS = Object.freeze({
  OFFLINE: 'offline',
  CONNECTING: 'connecting',
  READY: 'ready',
  RINGING: 'ringing',
  ACTIVE: 'active',
  WRAP_UP: 'wrapUp',
  OTHER_TAB: 'otherTab',
  ERROR: 'error'
})

/**
 * Which tab this store is. The softphone lives in the header of every dashboard tab, but exactly
 * one of them OWNS the transport and may touch a call; the rest are MIRRORS that render the owner's
 * broadcasts and ask the owner to act. A store that has not been told which it is does neither.
 *
 * The role is not fixed for the life of a tab: a mirror inherits ownership when the owner's tab
 * goes away, and an owner becomes a mirror when the agent takes the softphone over somewhere else.
 * See `services/softphone/softphoneOwnership.js` for how the two are kept to exactly one.
 */
export const SOFTPHONE_ROLES = Object.freeze({
  OWNER: 'owner',
  MIRROR: 'mirror'
})

/**
 * Post-call wrap-up. An answered call that runs past WRAP_UP_ELIGIBLE_CALL_DURATION_IN_MS earns the
 * agent a breather: the widget pauses the live campaign server-side while the call is still up, and
 * once it ends holds WRAP_UP_DURATION_IN_MS before resuming the campaign. Shorter calls (and declines)
 * go straight back to Ready - the campaign was never paused, so there is nothing to wait for.
 *
 * The pause happens DURING the call rather than at hang-up so that a call already being routed as
 * the agent hangs up finds the campaign paused, not a widget that is about to stop answering.
 */
export const WRAP_UP_ELIGIBLE_CALL_DURATION_IN_MS = 15000
export const WRAP_UP_DURATION_IN_MS = 60000

const STATUS_LABELS = {
  [SOFTPHONE_STATUS.OFFLINE]: 'Offline',
  [SOFTPHONE_STATUS.CONNECTING]: 'Connecting',
  [SOFTPHONE_STATUS.READY]: 'Ready',
  [SOFTPHONE_STATUS.RINGING]: 'Incoming',
  [SOFTPHONE_STATUS.ACTIVE]: 'On call',
  [SOFTPHONE_STATUS.WRAP_UP]: 'Wrap-up',
  [SOFTPHONE_STATUS.OTHER_TAB]: 'Other tab',
  [SOFTPHONE_STATUS.ERROR]: 'Error'
}

// Transport, ownership lock and timers are deliberately module scoped: they are neither
// serialisable nor reactive, and keeping them out of state means strict mode never sees them mutate.
let transport = null
let ownership = null
let wrapUpTimerId = null
let wrapUpEligibilityTimerId = null

// Set for the duration of `goOffline`. Both transports emit UNREGISTERED from their own
// `disconnect()`, and that event now takes the agent offline itself - without this flag the
// teardown would re-enter itself and pause the campaign a second time.
let isGoingOffline = false
let channel = null

// The teardown in flight, if any. Logging out and unloading can both ask for it, and a teardown
// whose pause request fails can trigger a logout of its own - so a second request joins the first
// rather than starting over with a transport the first is still disconnecting.
let teardownPromise = null

// The channel's handlers are append-only, and a tab changes role at runtime, so they are registered
// once per channel and each one checks the role it is for. Registering them per role transition
// would stack a duplicate handler on every handover.
let areChannelHandlersRegistered = false

export function getSoftphoneChannel() {
  return channel
}

function clearWrapUpTimer() {
  if (wrapUpTimerId) {
    clearTimeout(wrapUpTimerId)
    wrapUpTimerId = null
  }
}

function clearWrapUpEligibilityTimer() {
  if (wrapUpEligibilityTimerId) {
    clearTimeout(wrapUpEligibilityTimerId)
    wrapUpEligibilityTimerId = null
  }
}

const ONLINE_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.CONNECTING,
  SOFTPHONE_STATUS.READY,
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE,
  SOFTPHONE_STATUS.WRAP_UP
])

const IN_CALL_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE
])

function _ensureChannel() {
  if (!channel) {
    channel = createSoftphoneChannel()
  }

  return channel
}

/**
 * Gives up everything this tab holds at the tab level: the ownership lock (or its place in the
 * queue for it) and the channel. Owner and mirror alike, and nothing here touches a call or the
 * server - see `teardownSoftphone` for what comes before it in the owner.
 */
function _disposeTabResources() {
  if (ownership) {
    ownership.destroy()
    ownership = null
  }

  if (channel) {
    channel.close()
    channel = null
    areChannelHandlersRegistered = false
  }
}

/**
 * Drops the transport without waiting for it. The unload path cannot wait: the Telnyx disconnect
 * settles on a socket event, and a page in `pagehide` is gone before that arrives. Listeners go
 * first so the disconnect's own UNREGISTERED cannot land in a store that has already been reset.
 */
function _dropTransportWithoutWaiting() {
  if (!transport) {
    return
  }

  const currentTransport = transport

  transport = null

  currentTransport.removeAllListeners()
  removeSoftphoneDevHook()

  try {
    Promise.resolve(currentTransport.disconnect()).catch(error => {
      console.error('Failed to disconnect softphone transport', error)
    })
  } catch (error) {
    console.error('Failed to disconnect softphone transport', error)
  }
}

/**
 * Wires every cross-tab message once, guarded by the role each one belongs to.
 *
 * The guards are the point. A tab that inherits ownership keeps the mirror handlers it registered on
 * boot, and an unguarded `onState` would then let it apply a snapshot over the call state it now
 * owns - the softphone equivalent of taking dictation from itself.
 */
function _ensureChannelHandlers({ dispatch, state }) {
  _ensureChannel()

  if (areChannelHandlersRegistered) {
    return channel
  }

  areChannelHandlersRegistered = true

  channel.onState(message => {
    if (state.role !== SOFTPHONE_ROLES.MIRROR) {
      return
    }

    dispatch('applyRemoteState', message.snapshot)
  })

  channel.onClosed(() => {
    if (state.role !== SOFTPHONE_ROLES.MIRROR) {
      return
    }

    dispatch('handleOwnerClosed')
  })

  channel.onHello(() => {
    if (state.role !== SOFTPHONE_ROLES.OWNER) {
      return
    }

    _broadcastState(state)
  })

  channel.onCommand(message => {
    if (state.role !== SOFTPHONE_ROLES.OWNER) {
      return
    }

    dispatch('handleRemoteCommand', message)
  })

  return channel
}

/**
 * The single-owner lock. Created on first use rather than at import time so a tab that never has a
 * softphone user in it never asks the browser for anything.
 */
function _ensureOwnership({ dispatch }) {
  if (ownership) {
    return ownership
  }

  ownership = createSoftphoneOwnership()

  // Only ever the handover case in practice: the boot grant is returned by `request()` itself, and
  // a steal is returned by `steal()`. A handover means the previous owner's tab is gone.
  ownership.onGranted = ({ isHandover }) => {
    if (isHandover) {
      dispatch('adoptSoftphoneOwnership')
    }
  }

  ownership.onLost = () => dispatch('handleTabClaimLost')

  return ownership
}

/**
 * The offer this tab makes after inheriting a softphone that was live: "you were live for X -
 * resume?". Shaped so it can be handed straight to `goOnline`, which is why it is the campaign row
 * shape rather than the flattened `liveCampaign` it is built from.
 */
function _buildResumePrompt(liveCampaign) {
  return {
    id: liveCampaign.id,
    name: liveCampaign.name,
    category: { name: liveCampaign.categoryName || '' },
    language: { id: liveCampaign.languageId || '', name: liveCampaign.languageName || '' },
    budget: {
      minimumDurationSelected: liveCampaign.minimumDurationSelected === true,
      minimumDurationInSeconds: Number(liveCampaign.minimumDurationInSeconds) || 0
    }
  }
}

/**
 * Everything a mirror needs to render. Deliberately a hand-built object rather than the whole state:
 * `role` must never travel, or a mirror would adopt the owner's role and believe it may answer.
 */
export function buildSoftphoneSnapshot(state) {
  return {
    status: state.status,
    liveCampaign: state.liveCampaign,
    incomingCall: state.incomingCall,
    activeCall: state.activeCall,
    wrapUpEndsAt: state.wrapUpEndsAt,
    isPausedForWrapUp: state.isPausedForWrapUp,
    isMuted: state.isMuted,
    lastError: state.lastError
  }
}

export function _broadcastState(state) {
  if (state.role !== SOFTPHONE_ROLES.OWNER || !channel) {
    return
  }

  channel.postState(buildSoftphoneSnapshot(state))
}

const state = {
  // null until `initSoftphone` has settled the election. See SOFTPHONE_ROLES.
  role: null,

  status: SOFTPHONE_STATUS.OFFLINE,

  /**
   * The campaign this tab inherited an abandoned softphone for, offered back to the agent as
   * "you were live for X - resume?".
   *
   * Set only on a handover, and only in the tab that won it. Ownership passing to a new tab must
   * NOT quietly re-register the softphone: the agent is looking at some other tab by then, and a
   * tab they are not watching would start ringing on its own. Shaped so it can be handed straight
   * to `goOnline`, and deliberately kept out of the broadcast snapshot - it belongs to the one tab
   * that has to make the offer.
   */
  resumePrompt: null,

  // The single campaign this agent is live for. Only one at a time, by product decision.
  liveCampaign: null,

  incomingCall: null,

  activeCall: null,

  wrapUpEndsAt: null,

  // True from the moment the wrap-up pause landed on the server until the wrap-up resume did. Kept
  // separately from `status` because a second call can arrive and end while it is set, and that
  // call's end must still run the wrap-up - otherwise the campaign would stay paused forever.
  isPausedForWrapUp: false,

  isMuted: false,

  lastError: null
}

/**
 * @type {import('vuex').GetterTree<typeof state>}
 */
const getters = {
  /**
   * ENTITLEMENT SEAM. Every softphone surface is gated on this getter and nothing else. It
   * delegates to the single softphone gate the app already has: the global SOFTPHONE_GOES_LIVE
   * setting AND the per-user `softphoneAgent` flag. Both fail closed, so the incoming-call UI
   * is hidden unless the softphone campaign flows are visible to this user too.
   */
  isSoftphoneAvailable(state, getters, rootState, rootGetters) {
    return rootGetters['globalSettings/isSoftphoneUser'] === true
  },

  isSoftphoneOwner(state) {
    return state.role === SOFTPHONE_ROLES.OWNER
  },

  /**
   * The agent is taking calls, but in a different tab. What the header widget says instead of the
   * call controls, alongside the offer to take the softphone over here.
   */
  isSoftphoneInAnotherTab(state) {
    return state.role === SOFTPHONE_ROLES.MIRROR && ONLINE_STATUSES.includes(state.status)
  },

  /**
   * Whether taking the softphone over in this tab is offered.
   *
   * Refused while the owner has a call on it. A takeover drops the owner's transport, and WebRTC
   * media cannot move between tabs, so it would hang up on the caller - and the agent clicking it
   * has no way to know that from here.
   */
  canTakeOverSoftphone(state) {
    return state.role === SOFTPHONE_ROLES.MIRROR && !IN_CALL_STATUSES.includes(state.status)
  },

  hasSoftphoneResumePrompt(state) {
    return Boolean(state.resumePrompt)
  },


  /**
   * What the live campaign's calls are about, which is what the widget names - the campaign's own
   * name is the buyer's bookkeeping and means nothing to the agent answering.
   */
  liveCampaignCategoryName(state) {
    return state.liveCampaign?.categoryName || ''
  },

  /**
   * The language the campaign's callers speak, as the short code the agent reads at a glance - EN,
   * ES. Empty when the campaign carries none, and the widget then simply leaves it out.
   */
  liveCampaignLanguageCode(state) {
    return String(state.liveCampaign?.languageId || '').toUpperCase()
  },

  /**
   * The same language written out - English, Spanish - which is what the widget shows the agent.
   * A code is a thing to decode; the name is read. Falls back to the code for a campaign that
   * travelled here without a name on it, and is empty only when the campaign carries no language
   * at all, which the widget then simply leaves out.
   */
  liveCampaignLanguageName(state, getters) {
    return state.liveCampaign?.languageName || getters.liveCampaignLanguageCode
  },

  /**
   * How long an answered call must run before it earns. Zero for a campaign with no minimum, which
   * the widget says in words rather than reporting "0 seconds" - that reads like a value that
   * failed to load.
   */
  liveCampaignMinimumDurationInSeconds(state) {
    if (!state.liveCampaign?.minimumDurationSelected) {
      return 0
    }

    return Math.max(0, Number(state.liveCampaign.minimumDurationInSeconds) || 0)
  },

  isSoftphoneOnline(state) {
    return ONLINE_STATUSES.includes(state.status)
  },

  isRinging(state) {
    return state.status === SOFTPHONE_STATUS.RINGING
  },

  isCallActive(state) {
    return state.status === SOFTPHONE_STATUS.ACTIVE
  },

  isWrappingUp(state) {
    return state.status === SOFTPHONE_STATUS.WRAP_UP
  },

  liveCampaignId(state) {
    return state.liveCampaign?.id || null
  },

  /**
   * How long an answered call must run before the widget shows the caller's full number; until then
   * it shows only the last four digits.
   *
   * The gate is the live campaign's MINIMUM DURATION - the point at which the call is billable, and
   * so the point at which the buyer has earned the number. A campaign without a minimum duration
   * has nothing to wait for, so `0`: the number is shown from the moment the call is answered.
   */
  callerIdRevealAfterSeconds(state) {
    if (!state.liveCampaign?.minimumDurationSelected) {
      return 0
    }

    return Math.max(0, Number(state.liveCampaign.minimumDurationInSeconds) || 0)
  },

  softphoneStatusLabel(state) {
    return STATUS_LABELS[state.status] || STATUS_LABELS[SOFTPHONE_STATUS.OFFLINE]
  }
}

/**
 * @type {import('vuex').ActionTree<typeof state>}
 */
const actions = {
  /**
   * Boots the softphone for this tab. Called once per dashboard tab, whether or not the agent ever
   * goes live - the tab has to know what the softphone is doing before it can render a header
   * badge or a campaigns list, and it has to be queued for ownership before the owner disappears.
   *
   * The lock is asked for HERE rather than in `goOnline`, because the thing that must be unique is
   * the tab holding the transport, not the call: two registered tabs would both ring.
   */
  async initSoftphone({ dispatch }) {
    const isOwner = await _ensureOwnership({ dispatch }).request()

    await dispatch(isOwner ? 'becomeOwner' : 'becomeMirror')

    return isOwner
  },

  /**
   * Adopt the owner role. Ownership itself is granted by the lock, not by this - dispatching it
   * without having won the lock is a test-only shortcut.
   */
  becomeOwner({ commit, dispatch, state }) {
    commit('SET_ROLE', SOFTPHONE_ROLES.OWNER)

    _ensureChannelHandlers({ dispatch, state })

    // Doubles as "there is an owner here now", which is what a mirror booting alongside this tab
    // needs before it can render anything but Offline.
    _broadcastState(state)
  },

  /**
   * Follow the owner. A mirror never creates a transport.
   */
  becomeMirror({ commit, dispatch, state }) {
    commit('SET_ROLE', SOFTPHONE_ROLES.MIRROR)

    _ensureChannelHandlers({ dispatch, state })

    // The owner may already be mid-call; ask where things stand rather than assume offline.
    channel.postHello()
  },

  /**
   * Take the softphone on in this tab, having just been given the lock for it.
   *
   * Shared by both ways ownership arrives at a tab that was mirroring: a HANDOVER, where the tab
   * that held it went away and the browser handed the lock on, and a TAKEOVER, where the agent
   * asked for the softphone here. Both leave the same thing behind - a campaign the other tab was
   * live for and no longer serving - and both account for it identically.
   *
   * The campaign is NOT resumed automatically. In the handover case ownership goes to whichever tab
   * the browser picked, which is not necessarily one the agent is looking at, and a tab that
   * started ringing by itself would drop calls the agent never knew had arrived. So the campaign is
   * paused - the other tab did not get to do it reliably, and a campaign left Active with no agent
   * behind it routes calls at nobody - and then offered back.
   */
  async adoptSoftphoneOwnership({ commit, dispatch, state }) {
    const inherited = state.liveCampaign

    commit('SET_ROLE', SOFTPHONE_ROLES.OWNER)

    _ensureChannelHandlers({ dispatch, state })

    // Pauses the campaign, tells the sibling mirrors and resets. They were watching the other tab,
    // not this one, so this is the only way they hear about it.
    await dispatch('handleOwnerGone')

    if (inherited) {
      commit('SET_RESUME_PROMPT', _buildResumePrompt(inherited))
    }
  },

  /**
   * Move the softphone to THIS tab, because the agent asked for it here. Deliberate and never
   * automatic: it takes the lock away from the owner, which drops that tab's transport.
   *
   * Guarded by `canTakeOverSoftphone` at the call site - a takeover mid-call would hang up on the
   * caller, since WebRTC media cannot follow the lock.
   */
  async takeOverSoftphone({ dispatch }) {
    const isOwner = await _ensureOwnership({ dispatch }).steal()

    if (!isOwner) {
      return false
    }

    await dispatch('adoptSoftphoneOwnership')

    return true
  },

  /**
   * Accepts the resume offer: go live again, in this tab, for the campaign the softphone was
   * inherited from. Both halves are needed - the server has to resume the campaign, and this tab
   * has to register - and the server call goes first, so a refused resume never leaves an agent
   * registered for a paused campaign.
   *
   * The offer is cleared only once the server has agreed. A refused resume leaves it on screen, so
   * the agent can try again rather than having the one way back to their campaign taken away.
   */
  async resumeSoftphoneFromPrompt({ commit, dispatch, state }) {
    const campaign = state.resumePrompt

    if (!campaign) {
      return
    }

    await dispatch('campaign/goLiveSoftphoneCampaign', { campaignId: campaign.id }, { root: true })

    commit('SET_RESUME_PROMPT', null)

    await dispatch('goOnline', campaign)
  },

  dismissSoftphoneResumePrompt({ commit }) {
    commit('SET_RESUME_PROMPT', null)
  },

  /**
   * Gives up the lock, so a sibling tab can take the softphone over at once instead of waiting for
   * the browser to notice this one is gone.
   */
  releaseSoftphoneOwnership() {
    if (ownership) {
      ownership.release()
    }
  },

  /**
   * Diagnostics: how many tabs hold the softphone lock, and how many are queued behind it. The
   * invariant this whole module exists for, made checkable - `heldCount` is never above 1.
   */
  describeSoftphoneOwnership({ dispatch }) {
    return _ensureOwnership({ dispatch }).describe()
  },

  applyRemoteState({ commit, dispatch, state }, snapshot) {
    if (!snapshot) {
      return
    }

    const didWrapUpPauseChange = Boolean(snapshot.isPausedForWrapUp) !== Boolean(state.isPausedForWrapUp)

    // The agent stopped being live for a campaign: the widget paused it server-side, so the row
    // this tab is showing as Active is now Paused. Covers the agent going offline from the widget
    // and the widget dropping offline by itself (a lost registration, a transport error).
    const didStopBeingLive = Boolean(state.liveCampaign) && !snapshot.liveCampaign

    commit('APPLY_REMOTE_STATE', snapshot)

    // The widget paused or resumed the campaign on the server, and it has no campaigns list to
    // refresh - this tab does. Best effort by construction (the action swallows its own failure).
    if (didWrapUpPauseChange || didStopBeingLive) {
      dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
    }
  },

  /**
   * The owner's tab shut down CLEANLY - it has already paused the campaign, so there is nothing to
   * do but drop back to offline and make every Go Live button clickable again.
   *
   * This is also what keeps the tab that inherits the lock from offering a pointless resume: a
   * clean goodbye clears `liveCampaign` in every mirror, and `adoptSoftphoneOwnership` reads that
   * to decide whether there is anything to account for.
   */
  handleOwnerClosed({ commit, dispatch, state }) {
    const wasLive = Boolean(state.liveCampaign)

    commit('RESET_SOFTPHONE')

    // The widget paused the campaign on its way out. Only this tab has the list showing that
    // campaign as Active, so only this tab can put the badge right.
    if (wasLive) {
      dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
    }
  },

  /**
   * The owner's tab disappeared without saying goodbye, and this tab found out by being handed the
   * lock. Reached when the tab is closed, reloaded or killed: the unload path tries to pause with a
   * keepalive request, but that is the one call that cannot be awaited or retried.
   *
   * So it pauses, because a campaign left Active with no agent behind it routes calls at nobody.
   * Pausing twice is harmless, which is what makes it safe to do here on a hunch.
   */
  async handleOwnerGone({ commit, dispatch, state }) {
    _ensureChannel()

    const wasPausedForWrapUp = state.isPausedForWrapUp

    await dispatch('pauseLiveCampaign')

    // Sibling mirrors were not handed the lock, so they only learn about it from here - and they
    // refetch their campaigns list on hearing it. Said AFTER the pause has landed, so that refetch
    // sees the row Paused; said before, it would fetch a list that still reads Active, and the
    // offline snapshot broadcast by the reset below finds nothing left to change and never triggers
    // a second one.
    channel.postClosed()

    commit('RESET_SOFTPHONE')

    // The refetch inside the pause above ran while this store still believed a wrap-up was in
    // progress, so it kept the server's wrap-up label - and the server keeps that label until the
    // campaign is resumed, which now never happens. One more refetch, made after the reset, is what
    // turns the row from "Paused (wrap-up)" into plain "Paused".
    if (wasPausedForWrapUp) {
      dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
    }
  },

  async handleRemoteCommand({ dispatch }, { name, payload } = {}) {
    if (name === SOFTPHONE_COMMANDS.GO_ONLINE) {
      await dispatch('goOnline', payload)

      return
    }

    if (name === SOFTPHONE_COMMANDS.GO_OFFLINE) {
      // Going offline, not tearing down: the tab stays open and keeps the lock, because it is still
      // the one tab allowed to take calls and the agent may go live again in a moment.
      await dispatch('goOffline')
    }
  },

  /**
   * Go Live from wherever the agent clicked it.
   *
   * The softphone MOVES to the clicking tab. Anything else means the agent presses Go Live in the
   * tab they are working in and the call then rings in some other tab they may not even have on
   * screen. Moving it is safe here by construction: Go Live is only offered when nothing is live,
   * so the tab losing the lock has no transport and no call to drop.
   *
   * A mirror that cannot take the lock, or one whose view of the world says a call is up after all,
   * falls back to asking the owner. That fallback is not expected to be reached - the Go Live
   * button is dead while anything is live - and it exists because the alternative to a command
   * nobody acts on is a click that silently does nothing.
   */
  async requestGoOnline({ commit, dispatch, state }, campaign) {
    const payload = {
      id: campaign.id,
      name: campaign.name,
      // The category and the language are what the widget puts on screen. Narrowed here so the same
      // payload works whether it is handed to `goOnline` in this tab or structured-cloned to another.
      category: { name: campaign.category?.name || '' },
      language: { id: campaign.language?.id || '', name: campaign.language?.name || '' },
      // The minimum duration has to travel too: the widget gates the caller id on it (see
      // `callerIdRevealAfterSeconds`) and shows it beside the category. Narrowed to these fields
      // rather than passing the whole row, because this is structured-cloned on every go-live.
      budget: {
        minimumDurationSelected: campaign.budget?.minimumDurationSelected === true,
        minimumDurationInSeconds: Number(campaign.budget?.minimumDurationInSeconds) || 0
      }
    }

    const canMoveHere = state.role !== SOFTPHONE_ROLES.OWNER
      && !IN_CALL_STATUSES.includes(state.status)
      && await _ensureOwnership({ dispatch }).steal()

    if (canMoveHere) {
      commit('SET_ROLE', SOFTPHONE_ROLES.OWNER)

      _ensureChannelHandlers({ dispatch, state })

      // The mirrored view belongs to the tab that just lost the lock. Cleared rather than paused:
      // the server-side go-live behind this click has already settled which campaigns are live, and
      // a pause here would take the agent offline for the campaign they are going live for.
      commit('SET_RESUME_PROMPT', null)
      commit('RESET_SOFTPHONE')
    }

    if (state.role === SOFTPHONE_ROLES.OWNER) {
      return dispatch('goOnline', payload)
    }

    _ensureChannel()

    channel.postCommand(SOFTPHONE_COMMANDS.GO_ONLINE, payload)

    return undefined
  },

  /**
   * Go Offline from wherever the agent clicked it. Unlike Go Live this never moves the lock: the
   * tab that holds the transport is the only one that can disconnect it, and it pauses the campaign
   * on its way out, so a mirror asks rather than takes.
   */
  requestGoOffline({ dispatch, state }) {
    if (state.role === SOFTPHONE_ROLES.OWNER) {
      return dispatch('goOffline')
    }

    _ensureChannel()

    channel.postCommand(SOFTPHONE_COMMANDS.GO_OFFLINE)

    return Promise.resolve()
  },

  /**
   * Go live for exactly one campaign. Must be called from a user gesture: the ringtone is
   * armed here (see `ringtone.js`).
   */
  async goOnline({ commit, dispatch, state, rootGetters }, campaign) {
    if (!campaign?.id) {
      throw new Error('A campaign is required to go online')
    }

    // One campaign at a time: going online anywhere else takes the agent offline first.
    if (state.liveCampaign && state.liveCampaign.id !== campaign.id) {
      await dispatch('goOffline')
    } else if (state.liveCampaign?.id === campaign.id && state.status !== SOFTPHONE_STATUS.OFFLINE && state.status !== SOFTPHONE_STATUS.ERROR) {
      return
    }

    commit('SET_LAST_ERROR', null)
    // The category, the language and the minimum duration ride along because the widget shows the
    // first two and gates the caller id on the third (see `callerIdRevealAfterSeconds`), and the tab
    // holding the softphone may never fetch campaigns itself. `name` is kept for the campaign name
    // stamped onto each call.
    commit('SET_LIVE_CAMPAIGN', {
      id: campaign.id,
      name: campaign.name,
      categoryName: campaign.category?.name || '',
      languageId: campaign.language?.id || '',
      languageName: campaign.language?.name || '',
      minimumDurationSelected: campaign.budget?.minimumDurationSelected === true,
      minimumDurationInSeconds: Number(campaign.budget?.minimumDurationInSeconds) || 0
    })
    commit('SET_STATUS', SOFTPHONE_STATUS.CONNECTING)

    await armRingtone()

    try {
      transport = await createSoftphoneTransport()

      transport.on(SOFTPHONE_EVENTS.REGISTERED, () => dispatch('handleRegistered'))
      transport.on(SOFTPHONE_EVENTS.UNREGISTERED, () => dispatch('handleUnregistered'))
      transport.on(SOFTPHONE_EVENTS.INCOMING, payload => dispatch('handleIncomingCall', payload))
      transport.on(SOFTPHONE_EVENTS.ANSWERED, payload => dispatch('handleCallAnswered', payload))
      transport.on(SOFTPHONE_EVENTS.ENDED, payload => dispatch('handleCallEnded', payload))
      transport.on(SOFTPHONE_EVENTS.ERROR, payload => dispatch('handleTransportError', payload))

      exposeSoftphoneDevHook(transport)

      const token = transport.requiresToken
        ? (await SoftphoneAPI.getWebRtcToken(rootGetters['auth/accountId']))?.data?.token
        : null

      await transport.connect(token)
    } catch (error) {
      await dispatch('goOffline')

      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
      commit('SET_LAST_ERROR', error?.response?.data?.title || error?.message || 'Could not start taking calls')

      throw error
    }
  },

  /**
   * Going offline is the OWNER's job end to end, including pausing the campaign. The tab that
   * resumed it may be gone by now - the owner's tab closing has to leave the campaign paused
   * whether or not anything else is still open.
   */
  async goOffline({ commit, dispatch, state }, { isUnloading = false } = {}) {
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()
    stopRinging()

    isGoingOffline = true

    // Read before the pause, which resets it - see the second refetch at the end.
    const wasPausedForWrapUp = state.isPausedForWrapUp

    try {
      await dispatch('pauseLiveCampaign', { isUnloading })

      if (transport) {
        const currentTransport = transport

        transport = null

        try {
          await currentTransport.disconnect()
        } catch (error) {
          console.error('Failed to disconnect softphone transport', error)
        }

        currentTransport.removeAllListeners()
        removeSoftphoneDevHook()
      }

      // The ownership lock is NOT released here. Going offline leaves this tab open and still the
      // one allowed to take calls; the lock is held until the tab itself goes away. Releasing it
      // would hand the softphone to whichever tab happens to be queued next, for no reason.
      commit('RESET_SOFTPHONE')

      /**
       * The last error goes with the shift it belonged to. Every failure path sets its reason AFTER
       * calling this action - a lost registration, a transport error, a wrap-up resume that failed -
       * so clearing it here cannot swallow one; what it clears is the reason for a shift that has
       * already ended.
       *
       * It matters because an error is one of the two things that keep the header's toggle alive
       * while the status says Offline (see `canOpenPanel`). Left set, a shift the agent ended by
       * hand would go on offering them a panel with a stale explanation in it.
       */
      commit('SET_LAST_ERROR', null)

      /**
       * Going offline mid wrap-up: the row has to stop reading "Paused (wrap-up)" and read plain
       * "Paused", because the wrap-up is over - the agent ended it.
       *
       * It takes a SECOND refetch. The one inside the pause above ran while this store still
       * believed a wrap-up was in progress, so it kept the server's wrap-up label - and the server
       * keeps that label until the campaign is resumed, which now never happens (go-offline only
       * touches campaigns that are still Active, and this one is already paused). Made after the
       * reset, so `isCampaignPausedForWrapUp` finds no wrap-up to honour. Same reason and the same
       * remedy as `handleOwnerGone`.
       *
       * Not on the unload path: that tab is going away, and its pause is a keepalive request with
       * nothing left to render the answer.
       */
      if (wasPausedForWrapUp && !isUnloading) {
        dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
      }
    } finally {
      isGoingOffline = false
    }
  },

  /**
   * Tells the server the agent is offline, which pauses EVERY softphone campaign ringing them - not
   * just the one this window went live for - so calls stop being routed to a destination with nobody
   * behind it. Best effort: a failure here must not stop the transport from disconnecting, because
   * leaving the agent connected-but-offline is worse than a campaign that stays active.
   *
   * Skipped when this window never went live: it then has nothing to take offline, and a widget that
   * merely opened and closed must not pause campaigns an agent is live for elsewhere.
   */
  async pauseLiveCampaign({ dispatch, state, rootGetters }, { isUnloading = false } = {}) {
    if (!state.liveCampaign?.id) {
      return
    }

    const accountId = rootGetters['auth/accountId']

    // An XHR started in `beforeunload` is cancelled with the page, so the unload path uses fetch
    // with keepalive instead. It skips the axios auth-refresh interceptor - acceptable, because the
    // alternative is not pausing at all, and an expired token means the agent's session was already
    // over. Every other path keeps the interceptor.
    if (isUnloading) {
      goOfflineWithKeepalive(accountId, rootGetters['auth/accessToken'])

      return
    }

    try {
      // The root campaign action rather than SoftphoneAPI directly: importing the api module here
      // creates a cycle (api -> httpClient -> router -> store -> this module) that leaves the store
      // half-built at import time.
      await dispatch('campaign/goOfflineSoftphone', null, { root: true })
    } catch (error) {
      console.error('Failed to take the softphone agent offline', error)
    }
  },

  /**
   * Full teardown for a tab that is going away - closed, reloaded or logged out. Losing the tab
   * counts as going offline, but only in the OWNER: a mirror renders the owner's campaign without
   * holding it, and a mirror that paused on its way out would take the agent offline in the tab
   * that is still on the call. A mirror just leaves the queue for the lock and closes its channel.
   *
   * `isUnloading` is the path that cannot wait for anything - `pagehide`, and a logout whose
   * session may already be dead. It pauses with a keepalive request, says goodbye, releases the lock
   * and only then drops the transport, without awaiting it: the Telnyx disconnect settles on a
   * socket event, and a page that is unloading is gone before that arrives. Awaiting it left the
   * goodbye and the release unsent, so every close of a live tab looked to its siblings like a
   * crash - a second pause and a "take calls from here?" the agent had not earned.
   *
   * The ordering of pause, goodbye, release carries meaning. A `closed` message means this tab
   * paused and there is nothing left to do; a tab that vanishes WITHOUT one leaves the mirrors
   * holding its last state, and whichever of them inherits the lock pauses on its behalf (see
   * `handleOwnerGone`). Announcing before pausing would make the two indistinguishable.
   *
   * Re-entrant: a second call while one is in flight joins it. A logout can arrive mid-teardown
   * when the pause request itself fails to authenticate.
   */
  teardownSoftphone({ commit, dispatch, state }, { isUnloading = false } = {}) {
    if (teardownPromise) {
      return teardownPromise
    }

    const run = async () => {
      // Captured up front: a logout resets the whole store while this is in flight.
      const wasOwner = state.role === SOFTPHONE_ROLES.OWNER

      if (!wasOwner) {
        _disposeTabResources()
        commit('SET_ROLE', null)

        return
      }

      if (!isUnloading) {
        await dispatch('goOffline')

        if (channel) {
          channel.postClosed()
        }

        _disposeTabResources()
        commit('SET_ROLE', null)

        return
      }

      clearWrapUpTimer()
      clearWrapUpEligibilityTimer()
      stopRinging()

      // Synchronous in effect: the keepalive request is handed to the browser before this returns.
      await dispatch('pauseLiveCampaign', { isUnloading: true })

      if (channel) {
        channel.postClosed()
      }

      commit('RESET_SOFTPHONE')

      _disposeTabResources()
      _dropTransportWithoutWaiting()

      // Neither role now. Cleared last so the reset above still went out as the owner's word, and so
      // a second teardown - a logout's safety net after the deliberate one - finds nothing to do.
      commit('SET_ROLE', null)
    }

    teardownPromise = run().finally(() => {
      teardownPromise = null
    })

    return teardownPromise
  },

  async answerCall({ state }) {
    if (!state.incomingCall || !transport) {
      return
    }

    await transport.answer(state.incomingCall.id)
  },

  async declineCall({ state }) {
    if (!state.incomingCall || !transport) {
      return
    }

    await transport.reject(state.incomingCall.id)
  },

  async hangUp({ state }) {
    if (!state.activeCall || !transport) {
      return
    }

    await transport.hangup(state.activeCall.id)
  },

  async setMuted({ commit }, isMuted) {
    if (transport) {
      await transport.setMuted(isMuted)
    }

    commit('SET_MUTED', isMuted)
  },

  handleRegistered({ commit, state }) {
    if (state.status === SOFTPHONE_STATUS.CONNECTING) {
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)
    }
  },

  /**
   * The registration is gone, so Telnyx has nowhere to send a call - but the campaign is still
   * live, and Ringba would keep routing calls at a destination that cannot answer them. Taking the
   * agent offline is therefore part of handling this event, not just a status change: it pauses the
   * campaign and puts the Go Live buttons back so the agent can reconnect deliberately.
   */
  async handleUnregistered({ commit, dispatch, state }) {
    if (state.status === SOFTPHONE_STATUS.OFFLINE) {
      return
    }

    // Already tearing down - this event IS that teardown's disconnect. RESET_SOFTPHONE at the end
    // of it sets the status; setting it here keeps the old behaviour for the interim.
    if (isGoingOffline) {
      commit('SET_STATUS', SOFTPHONE_STATUS.OFFLINE)

      return
    }

    await dispatch('goOffline')

    // After goOffline, which resets the state: the agent needs to know their campaign is paused
    // and why the widget went quiet.
    commit('SET_LAST_ERROR', 'The connection was lost, so your campaign has been paused. Go live again when you are ready to take calls.')
  },

  /**
   * A call can still land during wrap-up: one that was already being routed when the campaign
   * paused. It is taken like any other, and `isPausedForWrapUp` stays set so its end runs the
   * wrap-up again and the campaign is eventually resumed.
   */
  handleIncomingCall({ commit, state }, { callId, campaignName, callerNumber } = {}) {
    clearWrapUpTimer()

    commit('SET_WRAP_UP_ENDS_AT', null)
    commit('SET_INCOMING_CALL', {
      id: callId,
      // v1 shows the campaign and nothing else; the transport has no campaign of its own.
      campaignName: campaignName || state.liveCampaign?.name || '',
      // Carried but deliberately NOT shown while ringing - the widget only reveals it on an
      // answered call. See `callerId` in SoftphoneWidget.
      callerNumber: callerNumber || null,
      receivedAt: Date.now()
    })
    commit('SET_STATUS', SOFTPHONE_STATUS.RINGING)

    startRinging()
  },

  handleCallAnswered({ commit, dispatch, state }, { callId, callerNumber } = {}) {
    stopRinging()

    commit('SET_ACTIVE_CALL', {
      id: callId || state.incomingCall?.id,
      campaignName: state.incomingCall?.campaignName || state.liveCampaign?.name || '',
      // The ringing call already carried it; the answered event repeats it, so prefer whichever
      // arrived - a call picked up in another window has no local incomingCall to read from.
      callerNumber: callerNumber || state.incomingCall?.callerNumber || null,
      answeredAt: Date.now()
    })
    commit('SET_INCOMING_CALL', null)
    commit('SET_MUTED', false)
    commit('SET_STATUS', SOFTPHONE_STATUS.ACTIVE)

    // A call that is still up this long earns a wrap-up; the campaign is paused now, mid-call, so
    // nothing new is routed at an agent who is about to need a break. See WRAP_UP_* above.
    clearWrapUpEligibilityTimer()
    wrapUpEligibilityTimerId = setTimeout(() => dispatch('pauseForWrapUp'), WRAP_UP_ELIGIBLE_CALL_DURATION_IN_MS)
  },

  /**
   * Pauses the live campaign server-side because the current call has run long enough to earn a
   * wrap-up. Best effort: a failure leaves the call untouched and the agent told, and the call then
   * ends straight into Ready as a short one would.
   */
  async pauseForWrapUp({ commit, dispatch, state }) {
    clearWrapUpEligibilityTimer()

    if (state.status !== SOFTPHONE_STATUS.ACTIVE || state.isPausedForWrapUp || !state.liveCampaign?.id) {
      return
    }

    try {
      await dispatch('campaign/pauseSoftphoneForWrapUp', null, { root: true })
    } catch (error) {
      console.error('Failed to pause the campaign for wrap-up', error)

      commit('SET_LAST_ERROR', 'Could not pause the campaign for wrap-up - the next call may arrive as soon as this one ends')

      return
    }

    // The request was in flight for a while; the world may have moved on. An agent who went offline
    // meanwhile wanted the campaign paused anyway, and the reset already cleared this flag - setting
    // it now would leave a stale "paused for wrap-up" on an offline widget.
    if (!ONLINE_STATUSES.includes(state.status)) {
      return
    }

    commit('SET_PAUSED_FOR_WRAP_UP', true)

    /**
     * The list is refetched HERE, after the flag, and that ordering is the whole point.
     *
     * "Paused (wrap-up)" is resolved as each row arrives, against this store's own state - see
     * `isCampaignPausedForWrapUp`. A refetch made before the flag lands strips the label instead of
     * showing it, which is exactly what a refetch inside `pauseSoftphoneForWrapUp` used to do.
     *
     * It never showed while the softphone lived in a window of its own: that window had no list, so
     * its refetch was a no-op and the dashboard tab picked the label up from the broadcast below.
     * The owning tab now has the list in front of the agent and has to refresh it itself - the
     * broadcast only ever reaches the OTHER tabs.
     */
    dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })

    // The call ended while the pause was in flight and handleCallEnded, seeing no pause yet, went
    // back to Ready. The campaign IS paused now, so the wrap-up has to start here or nothing would
    // ever resume it.
    if (state.status === SOFTPHONE_STATUS.READY) {
      dispatch('beginWrapUp')
    }
  },

  handleCallEnded({ commit, dispatch, state }) {
    stopRinging()
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()

    commit('SET_INCOMING_CALL', null)
    commit('SET_ACTIVE_CALL', null)
    commit('SET_MUTED', false)

    if (state.isPausedForWrapUp) {
      dispatch('beginWrapUp')

      return
    }

    // A short call, a decline, a missed call: the campaign was never paused, so the agent is simply
    // eligible for the next call. Anything else (offline, error, another tab) is left as it is.
    if (IN_CALL_STATUSES.includes(state.status) || state.status === SOFTPHONE_STATUS.WRAP_UP) {
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)
    }
  },

  beginWrapUp({ commit, dispatch }) {
    clearWrapUpTimer()

    commit('SET_WRAP_UP_ENDS_AT', Date.now() + WRAP_UP_DURATION_IN_MS)
    commit('SET_STATUS', SOFTPHONE_STATUS.WRAP_UP)

    wrapUpTimerId = setTimeout(() => dispatch('finishWrapUp'), WRAP_UP_DURATION_IN_MS)
  },

  /**
   * End of the wrap-up window: resume the campaign the agent was live on. A resume that fails leaves
   * a paused campaign behind a Ready-looking widget, which is the one state an agent cannot detect
   * from the widget alone, so it is handled like a failed goOnline - the widget goes offline with the
   * error showing and every Go Live button comes back so the agent can go live again.
   */
  async finishWrapUp({ commit, dispatch, state }) {
    clearWrapUpTimer()

    if (state.status !== SOFTPHONE_STATUS.WRAP_UP) {
      return
    }

    commit('SET_WRAP_UP_ENDS_AT', null)

    if (!state.isPausedForWrapUp) {
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)

      return
    }

    try {
      await dispatch('campaign/resumeSoftphoneAfterWrapUp', { campaignId: state.liveCampaign?.id }, { root: true })
    } catch (error) {
      console.error('Failed to resume the campaign after wrap-up', error)

      await dispatch('goOffline')

      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
      commit('SET_LAST_ERROR', error?.response?.data?.title || 'Could not resume the campaign after wrap-up. Go live again to keep taking calls.')

      return
    }

    if (state.status === SOFTPHONE_STATUS.WRAP_UP) {
      commit('SET_PAUSED_FOR_WRAP_UP', false)
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)

      return
    }

    // A call landed while the resume was in flight: the campaign is live again and the call is being
    // handled; only the flag needs clearing.
    if (IN_CALL_STATUSES.includes(state.status)) {
      commit('SET_PAUSED_FOR_WRAP_UP', false)

      return
    }

    // The agent went offline while the resume was in flight. Their go-offline and this go-live raced
    // on the server, and if the resume landed last the campaign is Active with nobody behind it.
    // Pausing again is harmless if the pause won; it is the only fix if it lost.
    try {
      await dispatch('campaign/goOfflineSoftphone', null, { root: true })
    } catch (pauseError) {
      console.error('Failed to re-pause the campaign after a wrap-up resume raced going offline', pauseError)
    }
  },

  /**
   * A transport error can mean the registration is gone (a dead websocket, a failed re-register),
   * and the same reasoning as handleUnregistered applies: a live campaign would go on having calls
   * routed at a browser that cannot take them. The campaign is paused first, then the error is
   * restored on top of the reset so the agent is left looking at what happened.
   */
  async handleTransportError({ commit, dispatch }, { message } = {}) {
    stopRinging()

    if (isGoingOffline) {
      commit('SET_LAST_ERROR', message || 'Connection error')
      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)

      return
    }

    await dispatch('goOffline')

    commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
    commit('SET_LAST_ERROR', message || 'Connection error')
  },

  /**
   * Another tab took the softphone over. Drop the transport without re-claiming, so the agent has
   * exactly one tab taking calls.
   *
   * Deliberately does NOT pause the campaign: the new owner is authoritative, and it pauses and
   * offers the campaign back itself (see `takeOverSoftphone`), so pausing here would fight it.
   *
   * This tab becomes an ordinary mirror rather than a dead one. The OTHER_TAB status is what it
   * shows until the new owner answers the hello below, which usually arrives immediately - it is
   * the visible state only when the takeover left nobody broadcasting.
   */
  async handleTabClaimLost({ commit, dispatch, state }) {
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()
    stopRinging()

    if (transport) {
      const currentTransport = transport

      transport = null

      // Listeners go BEFORE the disconnect, not after. Both transports emit UNREGISTERED from
      // their own `disconnect()`, and `handleUnregistered` takes the agent offline - which would
      // pause the campaign the new owner may have just gone live for, and leave this window
      // showing a lost-connection error for a handover that worked.
      currentTransport.removeAllListeners()

      try {
        await currentTransport.disconnect()
      } catch (error) {
        console.error('Failed to disconnect softphone transport', error)
      }

      removeSoftphoneDevHook()
    }

    // The role goes first, so the reset below is not broadcast as this tab's last word on a
    // softphone it no longer owns.
    commit('SET_ROLE', SOFTPHONE_ROLES.MIRROR)
    commit('RESET_SOFTPHONE')
    commit('SET_RESUME_PROMPT', null)
    commit('SET_STATUS', SOFTPHONE_STATUS.OTHER_TAB)

    _ensureChannelHandlers({ dispatch, state })

    channel.postHello()
  }
}

/**
 * @type {import('vuex').MutationTree<typeof state>}
 */
const mutations = {
  SET_ROLE(state, role) {
    state.role = role
  },

  /**
   * Applies a snapshot broadcast by the owner. Deliberately does NOT touch `role`: a mirror that
   * adopted the owner's role would start believing it may answer calls.
   */
  APPLY_REMOTE_STATE(state, snapshot) {
    state.status = snapshot.status
    state.liveCampaign = snapshot.liveCampaign
    state.incomingCall = snapshot.incomingCall
    state.activeCall = snapshot.activeCall
    state.wrapUpEndsAt = snapshot.wrapUpEndsAt
    state.isPausedForWrapUp = Boolean(snapshot.isPausedForWrapUp)
    state.isMuted = snapshot.isMuted
    state.lastError = snapshot.lastError
  },

  SET_STATUS(state, status) {
    state.status = status
  },

  SET_LIVE_CAMPAIGN(state, campaign) {
    state.liveCampaign = campaign
  },

  SET_INCOMING_CALL(state, call) {
    state.incomingCall = call
  },

  SET_ACTIVE_CALL(state, call) {
    state.activeCall = call
  },

  SET_WRAP_UP_ENDS_AT(state, wrapUpEndsAt) {
    state.wrapUpEndsAt = wrapUpEndsAt
  },

  SET_PAUSED_FOR_WRAP_UP(state, isPausedForWrapUp) {
    state.isPausedForWrapUp = isPausedForWrapUp
  },

  SET_MUTED(state, isMuted) {
    state.isMuted = isMuted
  },

  SET_LAST_ERROR(state, lastError) {
    state.lastError = lastError
  },

  SET_RESUME_PROMPT(state, resumePrompt) {
    state.resumePrompt = resumePrompt
  },

  // `role` is deliberately untouched: resetting the call state does not stop this tab owning the
  // softphone. Neither is `resumePrompt`, which outlives the reset by construction - it is set from
  // the state this very mutation clears.
  RESET_SOFTPHONE(state) {
    state.status = SOFTPHONE_STATUS.OFFLINE
    state.liveCampaign = null
    state.incomingCall = null
    state.activeCall = null
    state.wrapUpEndsAt = null
    state.isPausedForWrapUp = false
    state.isMuted = false
  }
}

// Test seam: lets a spec inject a FakeSoftphoneTransport without going through `goOnline`.
export const __testing = {
  setTransport(nextTransport) {
    transport = nextTransport
  },

  getTransport() {
    return transport
  },

  setOwnership(nextOwnership) {
    ownership = nextOwnership
  },

  getOwnership() {
    return ownership
  },

  hasPendingWrapUpTimer() {
    return wrapUpTimerId !== null
  },

  hasPendingWrapUpEligibilityTimer() {
    return wrapUpEligibilityTimerId !== null
  },

  setChannel(nextChannel) {
    channel = nextChannel
  },

  getChannel() {
    return channel
  },

  reset() {
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()
    transport = null
    ownership = null
    channel = null
    areChannelHandlersRegistered = false
    teardownPromise = null
    isGoingOffline = false
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
